/*
  LinkButton
  Button that behaves like a link, but is not an anchor tag.
  Why? SEO. Allows us to have multiple functional links to a URL 
  while only exposing one link to crawlers, and giving that link
  meaningful label text.

  CTA buttons tend to have generic text like "Enroll Now." Exposing  
  links with those labels is bad for SEO, as is having multiple
  links to the same url in close proximity.
*/
import * as React from 'react';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import type { TrackingData } from 'js/lib/retracked';

import type { ButtonProps } from '@coursera/cds-core';
import { Button } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { buildNewWindowOnClickViaUrl } from 'bundles/common/utils/newWindowUtils';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

export type Props = Omit<ButtonProps, 'children'> & {
  label: string;
  targetUrl: string;
  trackingName: string;
  trackingData?: TrackingData;
  forceOpenInNewTab?: boolean;
  tabIndex?: number;
  ariaHidden?: boolean;
  /**
   * If present, will override opening the `targetUrl`. Tracking will still take place.
   * If you need to open the URL conditionally in your `onClick` handler, use the exported `navigateOnClick` or
   * `openInNewWindow` functions, since just allowing default behavior won't do anything on a button.
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

export const openInNewWindow = (event: React.MouseEvent<HTMLButtonElement>, url: string) => {
  event.preventDefault();
  buildNewWindowOnClickViaUrl({ href: url })(event);
};

export const navigateOnClick = (event: React.MouseEvent<HTMLButtonElement>, url: string) => {
  event.preventDefault();
  if (event.ctrlKey || event.metaKey) return openInNewWindow(event, url);

  window.location.assign(url);
};

export default class LinkButton extends React.Component<Props> {
  openInNewWindowNoPropagation = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { targetUrl, onClick } = this.props;
    event.stopPropagation();
    onClick?.(event);
    openInNewWindow(event, targetUrl);
  };

  onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { targetUrl, onClick } = this.props;
    onClick?.(event);
    navigateOnClick(event, targetUrl);
  };

  render() {
    const {
      label,
      targetUrl,
      trackingData = {},
      trackingName,
      forceOpenInNewTab,
      tabIndex,
      ariaHidden,
      variant,
      size,
      type,
    } = this.props;

    const computedOnClick = forceOpenInNewTab ? this.openInNewWindowNoPropagation : this.onClick;

    return (
      <TrackedButton
        {...Button.defaultProps}
        size={size || 'medium'}
        trackingName={trackingName}
        trackingData={{ url: targetUrl, ...trackingData }}
        onClick={computedOnClick}
        onKeyPress={(event) => a11yKeyPress(event, computedOnClick)}
        variant={variant || 'secondary'}
        aria-hidden={ariaHidden || false}
        tabIndex={tabIndex || 0}
        role="link"
        type={type || 'button'}
      >
        {label}
      </TrackedButton>
    );
  }
}
